import React, { useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CalendarPlus, Trash2, Wand2, Info, Sparkles, Printer, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

/**
 * DrawGeneratorDialog
 * -------------------
 * Wizard for generating the entire tournament draw from the Groups page.
 * Configures group-stage days (with matches-per-team-per-day) and optional
 * QF/SF/Final reserved slots. Calls POST /api/schedule/generate-draws.
 */
const isoToday = () => new Date().toISOString().slice(0, 10);
const addDays = (dateStr, n) => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const defaultCourts = "Court 1, Court 2, Court 3";

const emptyDay = (date, timeSlots = "17:30, 20:00", courts = defaultCourts) => ({
  date: date || isoToday(),
  time_slots: timeSlots,       // e.g. "17:30, 20:00" — explicit slots (preferred)
  slot_minutes: 90,             // still used for conflict-window scaling
  courts,
});

const nextThursday = () => {
  const d = new Date();
  const day = d.getDay(); // 0=Sun ... 4=Thu
  const delta = (4 - day + 7) % 7 || 7; // always land on the *next* Thursday, not today
  d.setDate(d.getDate() + delta);
  return d.toISOString().slice(0, 10);
};

const MARA_PRESET = () => {
  // Mara Padel League operating template anchored to the *next* Thursday:
  // Thu 17:30/20:00 · Fri 17:30/20:00 · Sat 11:30/14:00 · QF Sat 17:30 · SF Sun 11:30 · Final Sun 17:30.
  const thu = nextThursday();
  return {
    days: [
      { date: thu, time_slots: "17:30, 20:00", slot_minutes: 90, courts: defaultCourts },
      { date: addDays(thu, 1), time_slots: "17:30, 20:00", slot_minutes: 90, courts: defaultCourts },
      { date: addDays(thu, 2), time_slots: "11:30, 14:00", slot_minutes: 90, courts: defaultCourts },
    ],
    qf: { date: addDays(thu, 2), start_at: "17:30", slot_minutes: 90, courts: "Court 1, Court 2" },
    sf: { date: addDays(thu, 3), start_at: "11:30", slot_minutes: 90, courts: "Court 1, Court 2" },
    fnl: { date: addDays(thu, 3), start_at: "17:30", slot_minutes: 90, courts: "Court 1" },
  };
};

const emptyKO = (date, start_at, slot_minutes = 90, matches_count = 4, courts = "Court 1, Court 2") => ({
  enabled: false,
  date: date || isoToday(),
  start_at,
  slot_minutes,
  courts,
  matches_count,
});

export default function DrawGeneratorDialog({ open, onOpenChange, groupsSummary = { A: 0, B: 0, C: 0 } }) {
  const navigate = useNavigate();
  const [days, setDays] = useState(() => [emptyDay(), emptyDay(addDays(isoToday(), 1)), emptyDay(addDays(isoToday(), 2), "11:30, 14:00")]);
  const [resetGroupStage, setResetGroupStage] = useState(true);
  const [qf, setQf] = useState(() => emptyKO(addDays(isoToday(), 2), "17:30", 90, 4, "Court 1, Court 2"));
  const [sf, setSf] = useState(() => emptyKO(addDays(isoToday(), 3), "11:30", 90, 2, "Court 1, Court 2"));
  const [fnl, setFnl] = useState(() => emptyKO(addDays(isoToday(), 3), "17:30", 90, 1, "Court 1"));
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  React.useEffect(() => { if (open) setDone(null); }, [open]);

  const downloadPdf = async () => {
    try {
      const resp = await api.get("/exports/tournament-draw.pdf", { responseType: "blob" });
      const href = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = href; a.download = "mara-draw-preview.pdf"; a.click();
      setTimeout(() => URL.revokeObjectURL(href), 5000);
    } catch (e) {
      toast.error("Failed to generate draw PDF");
    }
  };

  const applyMaraPreset = () => {
    const p = MARA_PRESET();
    setDays(p.days);
    setQf({ enabled: true, matches_count: 4, ...p.qf });
    setSf({ enabled: true, matches_count: 2, ...p.sf });
    setFnl({ enabled: true, matches_count: 1, ...p.fnl });
    toast.success("Mara League preset applied: Thu/Fri 17:30+20:00, Sat 11:30+14:00 · Knockouts Sat evening + Sun");
  };

  // Round-robin needs (N-1) rounds per group. Each round contributes C(N/2)
  // ties per group. Total ties across all groups = sum(C(k,2) for group sizes).
  // Since each tie takes one time slot (uses 3 courts simultaneously), the
  // total number of allocated slots across all days must be ≥ total ties.
  const maxTeamsInGroup = Math.max(groupsSummary.A || 0, groupsSummary.B || 0, groupsSummary.C || 0);
  const requiredRounds = maxTeamsInGroup >= 2 ? (maxTeamsInGroup % 2 === 0 ? maxTeamsInGroup - 1 : maxTeamsInGroup) : 0;
  const parseSlots = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
  const allocatedSlots = useMemo(
    () => days.reduce((sum, d) => sum + parseSlots(d.time_slots).length, 0),
    [days]
  );
  const totalTiesNeeded = useMemo(() => {
    let n = 0;
    ["A", "B", "C"].forEach((g) => {
      const k = groupsSummary[g] || 0;
      if (k >= 2) n += (k * (k - 1)) / 2;
    });
    return n;
  }, [groupsSummary]);
  const isFeasible = allocatedSlots >= totalTiesNeeded && totalTiesNeeded > 0;

  const totalGroupTies = totalTiesNeeded;
  const totalGroupMatches = totalGroupTies * 3; // each tie = 3 seed matches

  const updateDay = (i, patch) => setDays((prev) => prev.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  const removeDay = (i) => setDays((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));
  const addDay = () => {
    const last = days[days.length - 1];
    setDays((prev) => [...prev, emptyDay(last ? addDays(last.date, 1) : isoToday())]);
  };

  const generate = async () => {
    const parseCourts = (str) => (str || "").split(",").map((s) => s.trim()).filter(Boolean);
    setBusy(true);
    try {
      const body = {
        group_stage: {
          reset: resetGroupStage,
          days: days.map((d) => ({
            date: d.date,
            time_slots: parseSlots(d.time_slots),
            slot_minutes: parseInt(d.slot_minutes) || 90,
            courts: parseCourts(d.courts),
          })),
        },
      };
      const knockouts = {};
      const koPayload = (k) => ({
        date: k.date,
        start_at: k.start_at,
        slot_minutes: parseInt(k.slot_minutes) || 90,
        courts: parseCourts(k.courts),
        matches_count: Math.max(1, parseInt(k.matches_count) || 1),
      });
      if (qf.enabled) knockouts.quarterfinals = koPayload(qf);
      if (sf.enabled) knockouts.semifinals = koPayload(sf);
      if (fnl.enabled) knockouts.final = koPayload(fnl);
      if (Object.keys(knockouts).length) body.knockouts = knockouts;

      const { data } = await api.post("/schedule/generate-draws", body);
      const s = data?.summary || {};
      const okG = s.ties_ok ?? 0;
      const skG = s.ties_skipped ?? 0;
      const okK = s.ko_ok ?? 0;
      const skReasons = (data?.group_stage?.ties_created || [])
        .filter((t) => t.skipped)
        .slice(0, 6)
        .map((t) => `R${t.round} ${t.group}: ${t.team_a} vs ${t.team_b} — ${t.reason || "conflict"}`);
      if (okG === 0 && okK === 0) {
        toast.error(`Nothing was generated — ${skG} ties failed. ${skReasons[0] || "Check court conflicts."}`);
      } else if (skG > 0) {
        toast.warning(`Draws generated with ${skG} skipped — some ties clashed with existing bookings or court blocks`);
      } else {
        toast.success(`Draws generated · ${okG} group ties · ${okK} knockout matches`);
      }
      setDone({ ok_ties: okG, ok_ko: okK, skipped: skG, reasons: skReasons });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draw generation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl bg-[#0b1512] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="draw-generator-dialog">
        <DialogHeader>
          <DialogTitle className="font-anton text-3xl uppercase tracking-tight text-[#F4B400]">Create Draws</DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs">
            Configure days and knockouts to auto-generate every group tie and reserve QF/SF/Final slots.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-6" data-testid="draw-generator-success">
            {(() => {
              const nothing = (done.ok_ties + done.ok_ko) === 0;
              const partial = done.skipped > 0 && !nothing;
              const color = nothing ? "border-red-500/40 from-[#1a0808]" : partial ? "border-amber-500/40 from-[#1a1408]" : "border-emerald-500/40 from-[#0c1f18]";
              const iconBg = nothing ? "bg-red-500/20 border-red-500/40 text-red-400" : partial ? "bg-amber-500/20 border-amber-500/40 text-amber-400" : "bg-emerald-500/20 border-emerald-500/40 text-emerald-400";
              const title = nothing ? "Nothing Generated" : partial ? "Draws Generated (with warnings)" : "Draws Generated";
              return (
                <div className={`p-6 rounded-2xl border bg-gradient-to-b ${color} to-[#0f1a15] text-center`}>
                  <div className={`w-14 h-14 rounded-full border mx-auto flex items-center justify-center ${iconBg}`}>
                    <Wand2 className="w-6 h-6" />
                  </div>
                  <div className="mt-3 font-anton text-3xl uppercase tracking-tight text-white">{title}</div>
                  <div className="mt-1 text-sm text-zinc-400">
                    <b className={done.ok_ties > 0 ? "text-emerald-400" : "text-zinc-500"}>{done.ok_ties}</b> group ties ·{" "}
                    <b className={done.ok_ko > 0 ? "text-emerald-400" : "text-zinc-500"}>{done.ok_ko}</b> knockout matches
                    {done.skipped ? <> · <b className="text-amber-400">{done.skipped}</b> skipped</> : null}
                  </div>
                  {done.reasons && done.reasons.length > 0 && (
                    <div className="mt-4 mx-auto max-w-2xl p-3 rounded-lg bg-black/40 border border-white/10 text-left" data-testid="draw-skipped-reasons">
                      <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-2">Skipped ties — most common cause is court conflicts or availability blocks</div>
                      <ul className="text-xs text-zinc-400 space-y-1">
                        {done.reasons.map((r, i) => (<li key={i} className="truncate">• {r}</li>))}
                      </ul>
                      <div className="mt-2 text-[10px] text-zinc-500">
                        Fix: add more courts per day, spread ties over more time slots, or remove overlapping <a href="/admin/court-blocks" className="underline hover:text-white">court blocks</a>.
                      </div>
                    </div>
                  )}
                  <div className="mt-4 flex justify-center gap-2 flex-wrap">
                    {!nothing && (
                      <Button onClick={downloadPdf} className="bg-[#F4B400] text-black font-bold uppercase tracking-widest hover:bg-[#e5aa00]" data-testid="download-preview-pdf-btn">
                        <Printer className="w-4 h-4 mr-1.5" /> Download Preview PDF
                      </Button>
                    )}
                    <Button variant="outline" onClick={() => setDone(null)} data-testid="back-to-config-btn">
                      Back to Config
                    </Button>
                    {!nothing && (
                      <Button
                        variant="outline"
                        onClick={() => { onOpenChange(false); setTimeout(() => navigate("/schedule"), 200); }}
                        data-testid="goto-schedule-btn"
                      >
                        Go to Schedule <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        ) : (
        <>
        {/* Summary */}
        <Card className="p-4 bg-[#0f1a15] border-white/10">
          <div className="flex flex-wrap gap-4 text-sm">
            <div><span className="text-zinc-500">Group A:</span> <b className="text-white">{groupsSummary.A || 0} teams</b></div>
            <div><span className="text-zinc-500">Group B:</span> <b className="text-white">{groupsSummary.B || 0} teams</b></div>
            <div><span className="text-zinc-500">Group C:</span> <b className="text-white">{groupsSummary.C || 0} teams</b></div>
            <div className="ml-auto"><span className="text-zinc-500">Total group ties:</span> <b className="text-emerald-400">{totalGroupTies}</b> <span className="text-zinc-500">({totalGroupMatches} matches)</span></div>
          </div>
          <div className="mt-2 text-xs text-zinc-400 flex items-start gap-2">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#F4B400]" />
            Need <b className="text-white mx-1">{totalTiesNeeded} tie slot{totalTiesNeeded !== 1 ? "s" : ""}</b> (one tie = one time slot; 3 seed matches play in parallel on 3 courts). You've allocated
            <b className={`mx-1 ${isFeasible ? "text-emerald-400" : "text-red-400"}`}>{allocatedSlots} slot{allocatedSlots !== 1 ? "s" : ""}</b>
            across {days.length} day{days.length !== 1 ? "s" : ""}.
          </div>
        </Card>

        {/* Group Stage Days */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="font-anton text-xl uppercase tracking-tight text-white">Group Stage — Days</div>
            <div className="flex items-center gap-3">
              <Button size="sm" variant="outline" onClick={applyMaraPreset} className="text-xs border-[#F4B400]/50 text-[#F4B400] hover:bg-[#F4B400]/10" data-testid="apply-mara-preset-btn">
                <Sparkles className="w-3.5 h-3.5 mr-1" /> Mara League Preset
              </Button>
              <label className="flex items-center gap-2 text-xs text-zinc-400">
                <Switch checked={resetGroupStage} onCheckedChange={setResetGroupStage} data-testid="reset-group-stage-switch" />
                Reset existing group ties
              </label>
              <Button size="sm" variant="outline" onClick={addDay} className="text-xs" data-testid="add-day-btn">
                <CalendarPlus className="w-3.5 h-3.5 mr-1" /> Add Day
              </Button>
            </div>
          </div>

          <div className="space-y-3">
            {days.map((d, i) => (
              <Card key={i} className="p-3 bg-[#0f1a15] border-white/10" data-testid={`draw-day-row-${i}`}>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
                  <div>
                    <label className="text-[10px] uppercase text-zinc-500">Date</label>
                    <Input type="date" value={d.date} onChange={(e) => updateDay(i, { date: e.target.value })} className="bg-black/40 border-white/10 text-white" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase text-zinc-500">Time Slots (comma-separated)</label>
                    <Input value={d.time_slots} onChange={(e) => updateDay(i, { time_slots: e.target.value })} placeholder="17:30, 20:00" className="bg-black/40 border-white/10 text-white font-mono" data-testid={`draw-day-slots-${i}`} />
                    <div className="text-[9px] text-zinc-600 mt-0.5">{parseSlots(d.time_slots).length} slot{parseSlots(d.time_slots).length !== 1 ? "s" : ""} · max 1 tie per team per day</div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-[10px] uppercase text-zinc-500">Courts (comma-separated)</label>
                    <div className="flex gap-2">
                      <Input value={d.courts} onChange={(e) => updateDay(i, { courts: e.target.value })} placeholder="Court 1, Court 2, Court 3" className="bg-black/40 border-white/10 text-white flex-1" />
                      <Button variant="ghost" size="icon" onClick={() => removeDay(i)} disabled={days.length <= 1} className="text-red-400 hover:text-red-300" data-testid={`remove-day-btn-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Knockouts */}
        <div className="mt-6">
          <div className="font-anton text-xl uppercase tracking-tight text-white mb-2">Knockouts (optional)</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <KnockoutCard title="Quarterfinals" hint="Default 4 matches — top 8 seeds" state={qf} onChange={setQf} testId="ko-qf" />
            <KnockoutCard title="Semifinals" hint="Default 2 placeholder matches" state={sf} onChange={setSf} testId="ko-sf" />
            <KnockoutCard title="Grand Final" hint="Default 1 placeholder match" state={fnl} onChange={setFnl} testId="ko-final" />
          </div>
          <div className="mt-2 text-[11px] text-zinc-500 flex items-start gap-2">
            <Info className="w-3 h-3 mt-0.5 shrink-0" />
            SF and Final are created with top-seed placeholders. Re-assign the actual teams from the Schedule page after the group stage completes.
          </div>
        </div>

        <div className="mt-6 flex flex-col items-end gap-2">
          {!isFeasible && totalTiesNeeded > 0 && (
            <div
              className="text-[11px] text-amber-300 bg-amber-900/20 border border-amber-500/30 rounded-md px-3 py-1.5 max-w-full"
              data-testid="feasibility-warning-inline"
            >
              ⚠ Need <b>{totalTiesNeeded}</b> tie slots · you have <b>{allocatedSlots}</b>. Add <b>{Math.max(0, totalTiesNeeded - allocatedSlots)}</b> more slot{totalTiesNeeded - allocatedSlots !== 1 ? "s" : ""} — ties beyond capacity will be marked <b>skipped</b>.
            </div>
          )}
          <div className="flex justify-end gap-2 w-full">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-zinc-400">Cancel</Button>
            <Button onClick={generate} disabled={busy} className="bg-[#F4B400] text-black font-bold uppercase tracking-widest hover:bg-[#e5aa00]" data-testid="generate-draws-btn">
              <Wand2 className="w-4 h-4 mr-2" /> {busy ? "Generating..." : "Generate Draws"}
            </Button>
          </div>
        </div>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KnockoutCard({ title, hint, state, onChange, testId }) {
  return (
    <Card className={`p-3 border-white/10 ${state.enabled ? "bg-[#11241b]" : "bg-[#0f1a15] opacity-70"}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="font-anton text-base uppercase tracking-tight text-white">{title}</div>
          <div className="text-[10px] text-zinc-500">{hint}</div>
        </div>
        <Switch checked={state.enabled} onCheckedChange={(v) => onChange({ ...state, enabled: v })} data-testid={`${testId}-toggle`} />
      </div>
      {state.enabled && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Date</label>
              <Input type="date" value={state.date} onChange={(e) => onChange({ ...state, date: e.target.value })} className="bg-black/40 border-white/10 text-white text-xs" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Start</label>
              <Input type="time" value={state.start_at} onChange={(e) => onChange({ ...state, start_at: e.target.value })} className="bg-black/40 border-white/10 text-white text-xs" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase text-zinc-500">Slot (min)</label>
            <Input type="number" min={30} max={240} value={state.slot_minutes} onChange={(e) => onChange({ ...state, slot_minutes: e.target.value })} className="bg-black/40 border-white/10 text-white text-xs" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-zinc-500">Matches</label>
            <Input type="number" min={1} max={16} value={state.matches_count ?? 1} onChange={(e) => onChange({ ...state, matches_count: e.target.value })} className="bg-black/40 border-white/10 text-white text-xs" data-testid={`${testId}-matches`} />
          </div>
          <div>
            <label className="text-[10px] uppercase text-zinc-500">Courts</label>
            <Input value={state.courts} onChange={(e) => onChange({ ...state, courts: e.target.value })} placeholder="Court 1, Court 2" className="bg-black/40 border-white/10 text-white text-xs" />
          </div>
        </div>
      )}
    </Card>
  );
}
